import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import TenderSubmit from './pages/TenderSubmit';
import CvManagement from './pages/CvManagement';
import Employees from './pages/Employees';
import DpoDashboard from './pages/DpoDashboard';
import MailboxIngestion from './pages/MailboxIngestion';
import Analytics from './pages/Analytics';
import SkillDevelopment from './pages/SkillDevelopment';
import CategorySkillDetail from './pages/CategorySkillDetail';
import MySkills from './pages/MySkills';
import TeamDashboard from './pages/TeamDashboard';
import Admin from './pages/Admin';
import SkillAdmin from './pages/SkillAdmin';
import ProjectReportList from './pages/ProjectReportList';
import ProjectReportDetail from './pages/ProjectReportDetail';
import ProjectReportForm from './pages/ProjectReportForm';
// Versioned tender flow — v1 is the current default; v2 is opt-in during soft launch
import TenderListV1 from './pages/v1/TenderList';
import MatchResultsV1 from './pages/v1/MatchResults';
import TenderListPlaceholderV2 from './pages/v2/TenderListPlaceholder';
import TenderOverviewV2 from './pages/v2/TenderOverview';
import CandidateAnalysisV2 from './pages/v2/CandidateAnalysis';
import NotFound from './components/NotFound';
import { ProtectedRoute, useUserRoles } from './auth';

function DefaultRedirect() {
  const { roles, loaded } = useUserRoles();

  // While roles are loading, show nothing (avoids flash)
  if (!loaded) return null;

  // EMPLOYEE-only users go to My Skills; DPO-only users go to DPO dashboard
  const isOnlyEmployee = roles.length > 0 && roles.every((r) => r === 'EMPLOYEE');
  const isOnlyDpo = roles.length > 0 && roles.every((r) => r === 'DPO');

  if (isOnlyEmployee) return <Navigate to="/my-skills" replace />;
  if (isOnlyDpo) return <Navigate to="/dpo" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* Default redirect — role-aware */}
          <Route index element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />

          {/* Main application routes — wrapped in ProtectedRoute for Entra ID auth */}
          <Route path="dashboard" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'HR_ADMIN', 'ADMIN']}><Dashboard /></ProtectedRoute>} />

          {/* -------------------- Tender flow — versioned --------------------
              Default /tenders renders v1 (current UI). When v2 is solid,
              swap the two default lines below to render the v2 components.
              v1/ and v2/ paths remain explicit, stable destinations.
              ---------------------------------------------------------------- */}
          {/* DEFAULT — currently v1 */}
          <Route path="tenders" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><TenderListV1 /></ProtectedRoute>} />
          <Route path="tenders/:tenderId/matches" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><MatchResultsV1 /></ProtectedRoute>} />

          {/* v1 — explicit, stable pin (survives default flip) */}
          <Route path="v1/tenders" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><TenderListV1 /></ProtectedRoute>} />
          <Route path="v1/tenders/:tenderId/matches" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><MatchResultsV1 /></ProtectedRoute>} />

          {/* v2 — opt-in during soft launch */}
          <Route path="v2/tenders" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><TenderListPlaceholderV2 /></ProtectedRoute>} />
          <Route path="v2/tenders/:tenderId" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><TenderOverviewV2 /></ProtectedRoute>} />
          <Route path="v2/tenders/:tenderId/matches" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><CandidateAnalysisV2 /></ProtectedRoute>} />
          <Route path="v2/tenders/:tenderId/matches/:candidateId" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'ADMIN']}><CandidateAnalysisV2 /></ProtectedRoute>} />

          {/* Tender submit — unchanged, lives outside the versioned flow */}
          <Route path="tenders/submit" element={<ProtectedRoute roles={['TENDER_MANAGER', 'ADMIN']}><TenderSubmit /></ProtectedRoute>} />

          <Route path="employees" element={<ProtectedRoute roles={['TENDER_VIEWER', 'TENDER_MANAGER', 'HR_ADMIN', 'ADMIN']}><Employees /></ProtectedRoute>} />
          <Route path="project-reports" element={<ProtectedRoute roles={['EMPLOYEE', 'PROJECT_ADMIN', 'HR_ADMIN', 'ADMIN']}><ProjectReportList /></ProtectedRoute>} />
          <Route path="project-reports/new" element={<ProtectedRoute roles={['EMPLOYEE', 'PROJECT_ADMIN', 'ADMIN']}><ProjectReportForm /></ProtectedRoute>} />
          <Route path="project-reports/:id" element={<ProtectedRoute roles={['EMPLOYEE', 'PROJECT_ADMIN', 'HR_ADMIN', 'ADMIN']}><ProjectReportDetail /></ProtectedRoute>} />
          <Route path="project-reports/:id/edit" element={<ProtectedRoute roles={['EMPLOYEE', 'PROJECT_ADMIN', 'ADMIN']}><ProjectReportForm /></ProtectedRoute>} />
          <Route path="cvs" element={<ProtectedRoute roles={['TENDER_MANAGER', 'HR_ADMIN', 'ADMIN']}><CvManagement /></ProtectedRoute>} />
          <Route path="mailbox" element={<ProtectedRoute roles={['TENDER_MANAGER', 'ADMIN']}><MailboxIngestion /></ProtectedRoute>} />
          <Route path="analytics" element={<ProtectedRoute roles={['HR_ADMIN', 'ADMIN']}><Analytics /></ProtectedRoute>} />
          <Route path="skill-development" element={<ProtectedRoute roles={['HR_ADMIN', 'ADMIN']}><SkillDevelopment /></ProtectedRoute>} />
          <Route path="skill-development/detail" element={<ProtectedRoute roles={['HR_ADMIN', 'ADMIN']}><CategorySkillDetail /></ProtectedRoute>} />
          <Route path="my-team" element={<ProtectedRoute roles={['EMPLOYEE', 'ADMIN']}><TeamDashboard /></ProtectedRoute>} />
          <Route path="my-skills" element={<ProtectedRoute roles={['EMPLOYEE', 'ADMIN']}><MySkills /></ProtectedRoute>} />
          <Route path="dpo" element={<ProtectedRoute roles={['DPO', 'ADMIN']}><DpoDashboard /></ProtectedRoute>} />
          <Route path="admin" element={<ProtectedRoute roles={['ADMIN']}><Admin /></ProtectedRoute>} />
          <Route path="admin/skills" element={<ProtectedRoute roles={['ADMIN', 'PROJECT_ADMIN']}><SkillAdmin /></ProtectedRoute>} />

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
